/**
 * ログ管理システム
 * 開発環境と本番環境でログレベルを制御
 */

export enum LogLevel {
  ERROR = 0,
  WARN = 1,
  INFO = 2,
  DEBUG = 3
}

class Logger {
  private currentLevel: LogLevel;

  constructor() {
    // 環境に応じてログレベルを設定
    this.currentLevel = import.meta.env.DEV ? LogLevel.DEBUG : LogLevel.ERROR;
  }

  private shouldLog(level: LogLevel): boolean {
    return level <= this.currentLevel;
  }

  private formatMessage(level: string, category: string, message: string, data?: any): string {
    const timestamp = new Date().toISOString();
    const prefix = `[${timestamp}] ${level} [${category}]`;
    return data ? `${prefix} ${message}` : `${prefix} ${message}`;
  }

  error(category: string, message: string, error?: any) {
    if (this.shouldLog(LogLevel.ERROR)) {
      console.error(this.formatMessage('ERROR', category, message));
      if (error) {
        console.error('エラー詳細:', error);
      }
    }
  }

  warn(category: string, message: string, data?: any) {
    if (this.shouldLog(LogLevel.WARN)) {
      console.warn(this.formatMessage('WARN', category, message));
      if (data) {
        console.warn('データ:', data);
      }
    }
  }

  info(category: string, message: string, data?: any) {
    if (this.shouldLog(LogLevel.INFO)) {
      console.log(this.formatMessage('INFO', category, message));
      if (data) {
        console.log('データ:', data);
      }
    }
  }

  debug(category: string, message: string, data?: any) {
    if (this.shouldLog(LogLevel.DEBUG)) {
      console.log(this.formatMessage('DEBUG', category, message));
      if (data) {
        console.log('データ:', data);
      }
    }
  }

  // API関連の専用ログメソッド
  apiRequest(service: string, endpoint: string, data?: any) {
    this.debug('API', `${service} リクエスト送信: ${endpoint}`, data);
  }

  apiResponse(service: string, endpoint: string, success: boolean, data?: any) {
    if (success) {
      this.info('API', `${service} 成功: ${endpoint}`, data);
    } else {
      this.error('API', `${service} 失敗: ${endpoint}`, data);
    }
  }

  apiError(service: string, endpoint: string, error: any) {
    this.error('API', `${service} エラー: ${endpoint}`, error);
  }

  // 妖怪生成関連の専用ログメソッド
  yokaiGeneration(stage: string, message: string, data?: any) {
    this.info('YOKAI_GEN', `${stage}: ${message}`, data);
  }

  // ユーザーアクション関連
  userAction(action: string, details?: any) {
    this.info('USER', action, details);
  }
}

// シングルトンインスタンス
export const logger = new Logger();

// 開発環境でのみ使用するデバッグ関数
export const devLog = (category: string, message: string, data?: any) => {
  if (import.meta.env.DEV) {
    logger.debug(category, message, data);
  }
};